import { type ReactNode, useEffect } from 'react';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';

import { applyDssBodyClass } from './dss/dss';
import { Navbar } from './navbar/Navbar';
import { Sidebar } from './sidebar/Sidebar';
import { applyFavicon } from './skin/apply-favicon';
import { PageLoading } from './PageLoading';
import { applySkinBodyClass } from './skin/skin-body-class';
import { useShellSkin } from './skin/useShellSkin';

export interface ShellProps {
  readonly children: ReactNode;
}

/**
 * Moldura React que substitui o FullLayoutComponent: Sidebar ocupando a altura
 * total da tela e, à direita dela, Navbar com o conteúdo da rota abaixo, mais o
 * overlay de carregamento que a folha Angular aciona — sem modo primeiroAcesso
 * (fora de escopo). Não faz roteamento: a URL (incluindo /dss/) chega intacta
 * ao conteúdo.
 */
export function LayoutShell({ children }: ShellProps): JSX.Element {
  const { theme, cor, faviconRaw } = useShellSkin();

  useEffect(() => {
    applyFavicon(faviconRaw);
  }, [faviconRaw]);

  // Chrome e DSS são mutuamente exclusivos: só o DssShell aplica classes dss-style-*.
  // Este shell sempre marca not-dss-style e desfaz isso ao desmontar.
  useEffect(() => {
    applyDssBodyClass(false, null);
    return () => {
      document.body.classList.remove('not-dss-style');
    };
  }, []);

  // A folha Angular consome as variáveis --default-color* das classes body-<cor>
  // (_theme-colors.scss). Sem elas, botões como .btn-pri1 perdem a cor da conta.
  useEffect(() => {
    applySkinBodyClass(cor);
  }, [cor]);

  return (
    <ThemeProvider theme={theme}>
      {/* A casca cobre a viewport (position: fixed + inset: 0) e esconde o próprio
          overflow, então nenhum conteúdo mais alto/largo que a tela gera scroll de
          página — independentemente de margin/altura herdados do body. */}
      <Box
        className="layout-shell"
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'row',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar como primeira coluna: com o pai em row e stretch (padrão),
            ela ocupa a altura total da tela sem precisar de height explícito.
            Em mobile, a própria Sidebar não reserva espaço (vira overlay), então
            este slot não precisa de tratamento condicional aqui. */}
        <Box component="aside" data-slot="sidebar" sx={{ flexShrink: 0 }}>
          <Sidebar />
        </Box>
        {/* Coluna direita: Navbar restrita a essa largura (não mais à tela toda)
            e, abaixo dela, a folha Angular. `position: relative` ancora o overlay de
            carregamento, que cobre a coluna inteira (navbar incluída) e deixa a
            sidebar de fora, como no FullLayout. */}
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            px: 2,
          }}
        >
          {/* data-slot="navbar" ativa o mapeamento da fonte centernovo nesta subárvore
              (core/_react-shell-icons.scss), como já acontece no slot da sidebar. */}
          <Box data-slot="navbar" sx={{ flexShrink: 0 }}>
            <Navbar />
          </Box>
          {/* Único ponto de rolagem: se a folha Angular exceder a área, o scroll fica
              contido aqui (vertical), sem vazar para a página. overflowX oculto mata
              qualquer estouro lateral residual do conteúdo legado. */}
          <Box
            component="main"
            className="layout-shell__content"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              // O `.card` do legado traz `margin: 1rem 0` (bootstrap/_card.scss). Aqui o
              // recuo é só o horizontal da coluna (`px`), então o card não acrescenta
              // margem nenhuma. O seletor descendente vence a regra global por
              // especificidade, sem !important, e o efeito fica restrito à folha
              // montada nesta casca.
              '& .card': { margin: 0 },
            }}
          >
            {/* O conteúdo entra aqui sem key: quem o fornece decide quando remontar por
                navegação, e o remonte fica restrito a ele — Sidebar, Navbar e skin
                não remontam. */}
            {children}
          </Box>
          {/* Fora do <main>: dentro dele o overlay rolaria junto com o conteúdo. */}
          <PageLoading contained />
        </Box>
      </Box>
    </ThemeProvider>
  );
}
